/** @format */

import React, { useState } from 'react';
import { FiEye, FiEyeOff, FiLock } from 'react-icons/fi';

interface PasswordInputProps {
	id: string;
	name: string;
	label: string;
	value: string;
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	placeholder?: string;
	required?: boolean;
	error?: boolean;
	errorMessage?: string;
	strengthLabel?: string;
	disabled?: boolean;
	showComplexity?: boolean;
}

const getStrengthBarColor = (strength: string): string => {
	switch (strength) {
		case 'Weak':
			return 'bg-red-500 w-1/4';
		case 'Moderate':
			return 'bg-orange-500 w-2/4';
		case 'Strong':
			return 'bg-yellow-500 w-3/4';
		case 'Excellent':
			return 'bg-green-600 w-full';
		default:
			return 'bg-transparent w-0';
	}
};

const PasswordInput: React.FC<PasswordInputProps> = ({
	id,
	name,
	label,
	value,
	onChange,
	placeholder,
	required = false,
	error = false,
	errorMessage = `Field is required, ${label}`,
	strengthLabel,
	disabled = false,
	showComplexity = false,
}) => {
	const [showPassword, setShowPassword] = useState(false);

	const checks = [
		{ label: 'At least 8 characters', valid: value.length >= 8 },
		{ label: 'At least one number (0-9)', valid: /[0-9]/.test(value) },
		{
			label: 'At least one lowercase letter (a-z)',
			valid: /[a-z]/.test(value),
		},
		{
			label: 'At least one uppercase letter (A-Z)',
			valid: /[A-Z]/.test(value),
		},
		{
			label: 'At least one special character',
			valid: /[^A-Za-z0-9]/.test(value),
		},
	];

	const getComplexityBarClass = (count: number) => {
		switch (count) {
			case 0:
				return 'bg-transparent w-0';
			case 1:
				return 'bg-red-500 w-1/5';
			case 2:
				return 'bg-red-500 w-2/5';
			case 3:
				return 'bg-orange-500 w-3/5';
			case 4:
				return 'bg-yellow-500 w-4/5';
			case 5:
				return 'bg-green-600 w-full';
			default:
				return 'bg-transparent w-0';
		}
	};

	const barClass = showComplexity
		? getComplexityBarClass(checks.filter((c) => c.valid).length)
		: getStrengthBarColor(strengthLabel || '');

	return (
		<div className='mb-2.5'>
			<label
				htmlFor={id}
				className='textInputLabel'>
				{label}
				{required && <span className='textInputLabelRequired'>*</span>}
			</label>

			<div className='relative'>
				<span className='inputLeftIcon'>
					<FiLock size={20} />
				</span>

				<input
					type={showPassword ? 'text' : 'password'}
					id={id}
					name={name}
					value={value}
					onChange={onChange}
					placeholder={placeholder}
					className={`passwordInputCountainer ${
						error ? 'textInputContainerError' : 'textInputContainerActive'
					}`}
					autoComplete='off'
					disabled={disabled}
				/>

				<span
					className='passwordToggle'
					onClick={() => setShowPassword((prev) => !prev)}>
					{showPassword ? <FiEyeOff /> : <FiEye />}
				</span>
			</div>

			<div className='strengthContainer'>
				<div className={`strengthBar ${barClass}`} />
			</div>

			{strengthLabel && (
				<div className='strengthLabel'>
					Password strength:{' '}
					<span className='strengthValue'>{strengthLabel}</span>
				</div>
			)}

			{showComplexity && !checks.every((c) => c.valid) && (
				<div className='mt-2 ml-1 flex flex-col gap-0.5'>
					{checks.map((c, i) => (
						<p
							key={i}
							className={`text-xs flex items-center gap-1 ${
								c.valid ? 'text-green-600 font-medium' : 'text-gray-500'
							}`}>
							<span>{c.valid ? '✓' : '•'}</span>
							{c.label}
						</p>
					))}
				</div>
			)}

			{error && <p className='textInputErrorMessage'>{errorMessage}</p>}
		</div>
	);
};

export default PasswordInput;
